/** Full stationary quality is shared by every preset; only motion is cheaper. */
export type ProductViewerRenderQuality = "quality" | "balanced" | "performance";

export const DEFAULT_PRODUCT_VIEWER_RENDER_QUALITY: ProductViewerRenderQuality =
  "balanced";

/**
 * R3F 8/9 treats zero as "leave the current DPR alone" during configure().
 * The adaptive controller sets the real ratio before the first scene draw.
 * Otherwise every Canvas re-render resets motion DPR to its default [1, 2],
 * clearing the visible buffer between frames on each product rebuild.
 */
export const PRODUCT_VIEWER_MANAGED_DPR = 0;

/**
 * `shadowIntervalMs` throttles the floor shadow during animations (lids,
 * fits, view changes); `controlledShadowIntervalMs` applies while the user
 * controls the camera, where a throttled shadow visibly lags the orbit.
 */
export const PRODUCT_VIEWER_RENDER_QUALITY = {
  quality: {
    interactionPixelBudget: Number.POSITIVE_INFINITY,
    shadowIntervalMs: 0,
    controlledShadowIntervalMs: 0,
  },
  balanced: {
    interactionPixelBudget: 1_500_000,
    shadowIntervalMs: 50,
    controlledShadowIntervalMs: 0,
  },
  performance: {
    interactionPixelBudget: 750_000,
    shadowIntervalMs: 100,
    controlledShadowIntervalMs: 0,
  },
} as const;

/** Match the viewer's existing R3F [1, 2] DPR range, including at rest. */
export function productViewerDpr({
  width,
  height,
  deviceDpr,
  interacting,
  quality = DEFAULT_PRODUCT_VIEWER_RENDER_QUALITY,
}: {
  width: number;
  height: number;
  deviceDpr: number;
  interacting: boolean;
  quality?: ProductViewerRenderQuality;
}): number {
  const native = Math.min(
    2,
    Math.max(1, Number.isFinite(deviceDpr) ? deviceDpr : 1),
  );
  if (!interacting || width <= 0 || height <= 0) return native;
  const area = width * height;
  // Also catches NaN dimensions, which do not satisfy the comparisons above.
  if (!Number.isFinite(area)) return native;
  const budgetDpr = Math.sqrt(
    PRODUCT_VIEWER_RENDER_QUALITY[quality].interactionPixelBudget / area,
  );
  // Do not resample ordinary DPR-1 displays or quantize their native ratio.
  if (budgetDpr >= native) return native;
  // Two decimals keep tiny resize changes from churning large render targets.
  return Math.max(1, Math.floor(budgetDpr * 100) / 100);
}
